// components/ModifySponsors.tsx
import { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Radio,
  RadioGroup,
  Stack,
  VStack,
  HStack,
  Image,
  Text,
  useToast,
  IconButton,
  Select,
  Heading,
  Divider,
} from '@chakra-ui/react';
import { Sponsor, SponsorRequest } from '../Api/dto/Sponsors';
import { imageClient, sponsorClient } from '../Api/BackendClient2';
import { DeleteIcon, EditIcon, SaveIcon } from 'lucide-react';

export type SponsorContext = 'main' | 'base' | 'obstacle' | 'ofi' | 'hidden';  

export const sponsorContextMap = {
  main: "Huvudsponsor",
  base: "Sponsor",
  obstacle: "Hinderbana",
  ofi: "ÖFI sponsor (ej tävling)",
  hidden: "Dold/ej aktiv",
} as const;

export const allSponsorTypes = ['main', 'base', 'obstacle', 'ofi', 'hidden'] as Array<SponsorContext>;  
export const competitionSponsorTypes = ['main', 'base', 'obstacle'] as Array<SponsorContext>;  

export const competitionMainSponsorType = 'main';
export const competitionBaseSponsorType = 'base';
export const competitionObstacleSponsorType = 'obstacle';
export const notCompetitionSponsorType = 'ofi';
export const hiddenSponsorType = 'hidden';


interface ModifySponsorsProps {
  sponsors: Sponsor[];
  onAdded: (sponsor: Sponsor) => void;
}

export const ModifySponsors = (props: ModifySponsorsProps) => {
  const toast = useToast();

  // Create new sponsor state
  const [sponsorName, setSponsorName] = useState('');
  const [sponsorUrl, setSponsorUrl] = useState('');
  const [sponsorContext, setSponsorContext] = useState<SponsorContext>(competitionBaseSponsorType);
  const [sponsorImage, setSponsorImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  // Edit sponsor state
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingContext, setEditingContext] = useState<SponsorContext>(competitionBaseSponsorType);
  const [editingUrl, setEditingUrl] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState<number | null>(null);

  const showFormFields = sponsorName.trim().length > 0;

  const validateFile = (file: File) => {
      if (file.size > 5 * 2**20) { // 5 MiB (mibibyte, 5 * 1024 * 1024 bytes)
        toast({
          title: 'Filen är för stor',
          description: 'Vänligen välj en fil som är mindre än 5 MiB',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        throw new Error('File too large');
      }
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/svg+xml'];
      if (!validTypes.includes(file.type)) {
        toast({
          title: 'Fel filtyp',
          description: 'Endast JPG, PNG och SVG är tillåtna',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        throw new Error('Invalid file type');
      }
  }

  const setSelectionOfNewSponsorImage = (file: File) => {
    setSponsorImage(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  // Handle image selection
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
        try {
            validateFile(file);
        } catch (error) {
            return;
        }

      setSelectionOfNewSponsorImage(file);
    }
  };

  // Handle drag and drop
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.dataTransfer.files?.[0];
    if (file) {
        try {
            validateFile(file);
        } catch (error) {
            return;
        }

      setSelectionOfNewSponsorImage(file);
    }
  };

  // Create new sponsor
  const handleCreateSponsor = async () => {
    if (!sponsorName.trim() || !sponsorImage) {
      toast({
        title: 'Information saknas',
        description: 'Vänligen fyll i alla fält och välj en bild/logga',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsCreating(true);
    try {
      // Generate image filename
      const fileExtension = sponsorImage.name.split('.').pop();
      const imageName = `sponsor_${sponsorName.toLowerCase().replace(/\s+/g, '_')}.${fileExtension}`;

      // Upload image
      await imageClient.uploadImage(imageName, sponsorImage);

      // Create sponsor record
      const sponsorRequest: SponsorRequest = {
        name: sponsorName,
        url: sainitizeUrl(sponsorUrl),
        imageName,
        context: sponsorContext,
      };

      const createdSponsor = await sponsorClient.createSponsor(sponsorRequest);

      toast({
        title: 'Sponsor skapad',
        description: `${sponsorName} är nu skapad`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Reset form
      setSponsorName('');
      setSponsorUrl('');
      setSponsorContext(competitionBaseSponsorType);
      setSponsorImage(null);
      setImagePreview(null);

      props.onAdded(createdSponsor);
    } catch (error) {
      toast({
        title: 'Error creating sponsor',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsCreating(false);
    }
  };

  // Update sponsor context
  const handleUpdateSponsor = async (sponsor: Sponsor, newContext: SponsorContext, url: null| string) => {
    setIsUpdating(true);
    try {
      await sponsorClient.updateSponsor(sponsor.id, { context: newContext, url: sainitizeUrl(url || "")});

      toast({
        title: 'Sponsor uppdaterad',
        description: `${sponsor.name} context är uppdaterad`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setEditingId(null);
    } catch (error) {
      toast({
        title: 'Error updating sponsor',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  // Delete sponsor
  const handleDeleteSponsor = async (sponsor: Sponsor) => {
    setIsDeleting(sponsor.id);
    try {
      // Delete image
      await imageClient.deleteImage(sponsor.imageName).catch((error) => {
        console.error("Error deleting image:", error);
      });

      // Delete sponsor record
      await sponsorClient.deleteSponsor(sponsor.id);

      toast({
        title: 'Sponsor borttagen',
        description: `${sponsor.name} har tagits bort`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error deleting sponsor',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(null);
    }
  };

  return (
    <Box p={6}>
      {/* Create New Sponsor Section */}
      <Box mb={8} borderRadius={"10px"} borderWidth={"1px"} borderColor={"black"} p={6} boxShadow={"md"}>
        <Heading as="h2" size="lg" mb={6}>
          Skapa ny sponsor
        </Heading>

        <Stack spacing={4}>
          {/* Sponsor Name Input */}
          <FormControl>
            <FormLabel>Sponsor namn</FormLabel>
            <Input
              placeholder="Skriv sponsorns namn..."
              value={sponsorName}
              onChange={(e) => setSponsorName(e.target.value)}
              isDisabled={isCreating}
            />
          </FormControl>

          {/* Conditional Fields */}
          {showFormFields && (
            <>
              {/* Context Radio Group */}
              <FormControl>
                <FormLabel>Typ</FormLabel>
                <RadioGroup value={sponsorContext} onChange={(value) => setSponsorContext(value as SponsorContext)}>
                  <Stack direction="row">
                    {
                        allSponsorTypes.map(s => (
                            <Radio key={s} value={s}>{sponsorContextMap[s]}</Radio>
                        ))
                    }
                  </Stack>
                </RadioGroup>
              </FormControl>

              {/* URL Input */}
              <FormControl>
                <FormLabel>Hemsida URL</FormLabel>
                <Input
                  placeholder="https://example.com"
                  value={sponsorUrl}
                  onChange={(e) => setSponsorUrl(e.target.value)}
                  isDisabled={isCreating}
                />
              </FormControl>

              {/* Image Upload with Drag & Drop */}
              <FormControl>
                <FormLabel>Sponsor logotyp</FormLabel>
                <Box
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  border="2px dashed"
                  borderColor="gray.300"
                  borderRadius="md"
                  p={6}
                  textAlign="center"
                  cursor="pointer"
                  transition="all 0.2s"
                  _hover={{ borderColor: 'blue.400', bg: 'gray.50' }}
                >
                  <Input
                    type="file"
                    accept=".jpg,.jpeg,.png,.svg"
                    onChange={handleImageChange}
                    display="none"
                    id="image-input"
                    isDisabled={isCreating}
                  />
                  <FormLabel htmlFor="image-input" cursor="pointer" mb={0}>
                    <VStack spacing={2}>
                      <Text>Dra och släpp bild här eller klicka för att välja</Text>
                      <Text fontSize="sm" color="gray.500">
                        JPG, PNG eller SVG
                      </Text>
                    </VStack>
                  </FormLabel>
                </Box>

                {/* Image Preview */}
                {imagePreview && (
                  <Box mt={4}>
                    <Image
                      src={imagePreview}
                      alt="Preview"
                      maxH="200px"
                      maxW="200px"
                      objectFit="contain"
                    />
                  </Box>
                )}
              </FormControl>

              {/* Create Button */}
              <Button
                colorScheme="green"
                onClick={handleCreateSponsor}
                isLoading={isCreating}
                loadingText="Skapar..."
              >
                Skapa sponsor
              </Button>
            </>
          )}
        </Stack>
      </Box>

      <Divider my={8} />

      {/* Edit Sponsors Section */}
      <Box>
        <Heading as="h2" size="lg" mb={6}>
          Ändra sponsor
        </Heading>

        {props.sponsors.length === 0 ? (
          <Text color="gray.500">Inga sponsorer ännu</Text>
        ) : (
          <VStack spacing={6} align="stretch">
            {props.sponsors.map((sponsor) => (
              <Box key={sponsor.id} p={4} border="1px solid" borderColor="gray.200" borderRadius="md">
                <VStack spacing={4} align="stretch">
                  {/* Sponsor Image */}
                  <Image
                    src={imageClient.getImageUrl(sponsor.imageName)}
                    alt={sponsor.name}
                    maxH="150px"
                    maxW="150px"
                    objectFit="contain"
                  />

                  {/* Sponsor URL */}
                  <Box>
                    <Text fontWeight="bold" mb={1}>
                      {sponsor.name}
                    </Text>
                    <Text fontSize="sm" color="blue.600">
                      <a href={sponsor.url} target="_blank" rel="noopener noreferrer">
                        {sponsor.url}
                      </a>
                    </Text>
                  </Box>

                  {/* Edit Controls */}
                  <HStack spacing={4}>
                    {/* Context Selector */}
                    {editingId === sponsor.id ? (
                      <FormControl>
                        <Select
                          value={editingContext}
                          onChange={(e) => {
                            setEditingContext(e.target.value as SponsorContext);
                            sponsor.context = e.target.value;
                        }}
                          isDisabled={isUpdating}
                        >
                            {
                                allSponsorTypes.map(s => {
                                    return <option key={s} value={s}>{sponsorContextMap[s]}</option>;
                                })
                            }
                        </Select>
                        <Input placeholder='Ange URL/hemsida här' value={editingUrl ?? ""} onChange={(e) => {
                            setEditingUrl(e.target.value);
                            sponsor.url = e.target.value;
                          }}/>
                      </FormControl>
                    ) : (
                      <Box flex={1}>
                        <Text fontSize="sm" color="gray.600">
                          Visa som: <strong>{sponsorContextMap[sponsor.context as keyof typeof sponsorContextMap]}</strong>
                        </Text>
                      </Box>
                    )}

                    {/* Edit/Save Button */}
                    <IconButton
                      aria-label={editingId === sponsor.id ? 'Save' : 'Edit'}
                      icon={editingId === sponsor.id ? <SaveIcon /> : <EditIcon />}
                      onClick={() => {
                        if (editingId === sponsor.id) {
                          handleUpdateSponsor(sponsor, editingContext, editingUrl);
                        } else {
                          setEditingId(sponsor.id);
                          setEditingContext(sponsor.context as SponsorContext);
                          setEditingUrl(sponsor.url);
                        }
                      }}
                      isLoading={isUpdating && editingId === sponsor.id}
                      colorScheme={editingId === sponsor.id ? 'green' : 'blue'}
                    >
                      {editingId === sponsor.id ? '✓' : undefined}
                    </IconButton>

                    {/* Delete Button */}
                    <IconButton
                      aria-label="Ta bort sponsor"
                      icon={<DeleteIcon />}
                      onClick={() => handleDeleteSponsor(sponsor)}
                      isLoading={isDeleting === sponsor.id}
                      colorScheme="red"
                      variant="outline"
                    />
                  </HStack>
                </VStack>
              </Box>
            ))}
          </VStack>
        )}
      </Box>
    </Box>
  );
};


function sainitizeUrl(url: string) {
    if (!url) {
      return url;
    }

    url.replaceAll(/\s/g, "");
    if (url.startsWith("https://") || url.startsWith("http://") || url.startsWith("www.")) {
      return url;
    }

    return `https://${url}`;
}